import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Eye, EyeOff, CheckCircle2, Check, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authToast } from '@/lib/toast/authToast';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { MicrosoftIcon } from '@/components/icons/MicrosoftIcon';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { loginSchema, type LoginFormValues } from './login.schema';
import { cn } from '@/lib/utils';
import { isFeatureEnabled } from '@/config/featureFlags';
import { getUserActiveMemberships } from '@/services/membershipIndex.service';

export default function LoginPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, resetPassword, signInWithGoogle, signInWithMicrosoft } =
    useAuth();

  // Get redirect URL from query params (set by ProtectedRoute)
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    mode: 'onChange',
  });

  const { formState, setFocus, watch } = form;
  const { errors, isValid } = formState;
  const emailValue = watch('email');
  const isEmailValid = emailValue && !errors.email && emailValue.includes('@');

  // Redirect if already authenticated
  // NOTE: We do NOT wait for TeamContext here because /login is outside TeamProvider.
  // After redirect, the guards (OnboardingGuard, TeamGuard) will handle routing
  // based on team/onboarding state once TeamProvider mounts.
  useEffect(() => {
    if (isAuthenticated) {
      logger.info('[LoginPage] Already authenticated, navigating to:', redirectTo);
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  // Focus first invalid field after failed submit
  useEffect(() => {
    if (hasSubmitError && Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0] as keyof LoginFormValues;
      setFocus(firstErrorField);
      setHasSubmitError(false);
    }
  }, [hasSubmitError, errors, setFocus]);

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setIsSubmitting(true);
      setLoginError(null);

      // Call Firebase Auth login with remember me preference
      await login(data.email, data.password, data.rememberMe);

      // Show success state
      setShowSuccess(true);

      // Success - show toast
      // Navigation will happen automatically via useEffect when teamLoading becomes false
      authToast.loginSuccess();
      logger.info('[LoginPage] Login successful, waiting for team state:', {
        email: data.email,
        rememberMe: data.rememberMe,
      });
    } catch (error) {
      // Handle Firebase Auth errors
      // SECURITY FIX C: Use generic error messages to prevent account enumeration
      // Do NOT reveal whether email exists or which provider was used
      let errorMessage = t('auth.signIn.errors.generic');
      let showPasswordResetHint = false;

      if (error && typeof error === 'object' && 'code' in error) {
        const code = error.code as string;
        // All credential-related errors get the same generic message
        if (
          code === 'auth/invalid-credential' ||
          code === 'auth/wrong-password' ||
          code === 'auth/user-not-found' ||
          code === 'auth/invalid-email'
        ) {
          // Generic message that doesn't reveal if account exists
          errorMessage = t(
            'auth.signIn.errors.invalidCredentialsGeneric',
            'Inloggen lukt niet. Controleer je gegevens of stel je wachtwoord opnieuw in.'
          );
          showPasswordResetHint = true;
        } else if (code === 'auth/user-disabled') {
          errorMessage = t('auth.signIn.errors.userDisabled');
        } else if (code === 'auth/too-many-requests') {
          errorMessage = t('auth.signIn.errors.tooManyRequests');
        } else if (code === 'auth/network-request-failed') {
          errorMessage = t('auth.signIn.errors.networkError');
        }
      }

      setLoginError(errorMessage);
      // Store hint state for showing additional guidance
      if (showPasswordResetHint) {
        setLoginError(
          errorMessage +
            '\n\n' +
            t(
              'auth.signIn.errors.oauthHint',
              'Gebruik je Google/Microsoft om in te loggen? Probeer die methode.'
            )
        );
      }
      logger.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidSubmit = () => {
    setHasSubmitError(true);
  };

  // Google Sign-In handler
  // LOGIN PAGE RULE: Only allow existing users with memberships. New users go to /signup/continue
  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setLoginError(null);

      const result = await signInWithGoogle();

      // CHECK: Does this user have any active memberships (i.e., a Modcar account)?
      // This is the source of truth - not isNewUser from Firebase Auth
      const memberships = await getUserActiveMemberships(result.user.uid);

      if (memberships.length === 0) {
        // No memberships = no Modcar account yet
        // Route to /signup/continue where they can choose to create account or use different account
        logger.info('[LoginPage] Google user has no memberships, routing to /signup/continue');
        navigate('/signup/continue');
        setIsGoogleLoading(false);
        return;
      }

      // User has memberships - proceed with normal login
      setShowSuccess(true);
      authToast.loginSuccess();
      logger.info('[LoginPage] Google sign-in successful for existing user with memberships');

      // Don't navigate directly - let the guards handle routing
      // The useEffect watching isAuthenticated will trigger navigation
    } catch (error) {
      // Handle Firebase Auth errors for Google sign-in
      let errorMessage = t('auth.signIn.errors.generic');

      if (error && typeof error === 'object' && 'code' in error) {
        const code = error.code as string;
        if (code === 'auth/account-exists-with-different-credential') {
          errorMessage = t('auth.signIn.errors.accountExistsWithDifferentCredential');
        } else if (code === 'auth/popup-closed-by-user') {
          // User closed the popup - don't show error, just reset state
          setIsGoogleLoading(false);
          return;
        } else if (code === 'auth/cancelled-popup-request') {
          // Multiple popup requests - ignore
          setIsGoogleLoading(false);
          return;
        } else if (code === 'auth/network-request-failed') {
          errorMessage = t('auth.signIn.errors.networkError');
        } else if (code === 'auth/user-disabled') {
          errorMessage = t('auth.signIn.errors.userDisabled');
        }
      }

      // Ignore redirect in progress error
      if (error instanceof Error && error.message === 'REDIRECT_IN_PROGRESS') {
        return;
      }

      setLoginError(errorMessage);
      logger.error('[LoginPage] Google sign-in error:', error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Microsoft Sign-In handler
  // LOGIN PAGE RULE: Only allow existing users with memberships. New users go to /signup/continue
  const handleMicrosoftSignIn = async () => {
    try {
      setIsMicrosoftLoading(true);
      setLoginError(null);

      const result = await signInWithMicrosoft();

      // CHECK: Does this user have any active memberships (i.e., a Modcar account)?
      // This is the source of truth - not isNewUser from Firebase Auth
      const memberships = await getUserActiveMemberships(result.user.uid);

      if (memberships.length === 0) {
        // No memberships = no Modcar account yet
        // Route to /signup/continue where they can choose to create account or use different account
        logger.info('[LoginPage] Microsoft user has no memberships, routing to /signup/continue');
        navigate('/signup/continue');
        setIsMicrosoftLoading(false);
        return;
      }

      // User has memberships - proceed with normal login
      setShowSuccess(true);
      authToast.loginSuccess();
      logger.info('[LoginPage] Microsoft sign-in successful for existing user with memberships');

      // Don't navigate directly - let the guards handle routing
    } catch (error) {
      // Handle Firebase Auth errors for Microsoft sign-in
      let errorMessage = t('auth.signIn.errors.generic');
      let errorCode = 'unknown';

      if (error && typeof error === 'object' && 'code' in error) {
        errorCode = error.code as string;
        if (errorCode === 'auth/account-exists-with-different-credential') {
          errorMessage = t('auth.signIn.errors.accountExistsWithDifferentCredential');
        } else if (errorCode === 'auth/popup-closed-by-user') {
          // User closed the popup - don't show error, just reset state
          setIsMicrosoftLoading(false);
          return;
        } else if (errorCode === 'auth/cancelled-popup-request') {
          // Multiple popup requests - ignore
          setIsMicrosoftLoading(false);
          return;
        } else if (errorCode === 'auth/network-request-failed') {
          errorMessage = t('auth.signIn.errors.networkError');
        } else if (errorCode === 'auth/user-disabled') {
          errorMessage = t('auth.signIn.errors.userDisabled');
        } else if (errorCode === 'auth/internal-error') {
          // Often indicates Azure AD config issue
          errorMessage = 'Microsoft login configuratiefout. Neem contact op met support.';
        }
      }

      // Ignore redirect in progress error
      if (error instanceof Error && error.message === 'REDIRECT_IN_PROGRESS') {
        return;
      }

      // In development, show error code for debugging
      if (import.meta.env.DEV) {
        errorMessage = `${errorMessage} (${errorCode})`;
      }

      setLoginError(errorMessage);
      logger.error('[LoginPage] Microsoft sign-in error:', { code: errorCode, error });
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      setResetError(t('auth.resetPassword.errors.invalidEmail'));
      return;
    }

    try {
      setIsResetting(true);
      setResetError(null);

      // Send password reset email via Firebase
      await resetPassword(resetEmail);

      setResetSuccess(true);
      authToast.resetEmailSent();

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowForgotPasswordModal(false);
        setResetSuccess(false);
        setResetEmail('');
      }, 2000);
    } catch (error) {
      // Handle Firebase errors
      // SECURITY FIX C: Use generic error messages to prevent account enumeration
      // Do NOT reveal whether email exists - always show success-like message
      // except for rate limiting and network errors
      let errorMessage = t('auth.resetPassword.errors.generic');

      if (error && typeof error === 'object' && 'code' in error) {
        const code = error.code as string;
        if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
          // SECURITY: Don't reveal if user exists - show success anyway
          // Firebase will simply not send an email if user doesn't exist
          setResetSuccess(true);
          authToast.resetEmailSent();
          setTimeout(() => {
            setShowForgotPasswordModal(false);
            setResetSuccess(false);
            setResetEmail('');
          }, 2000);
          return; // Exit early - don't show error
        } else if (code === 'auth/too-many-requests') {
          errorMessage = t('auth.resetPassword.errors.tooManyRequests');
        } else if (code === 'auth/network-request-failed') {
          errorMessage = t('auth.resetPassword.errors.networkError');
        }
      }

      setResetError(errorMessage);
      logger.error('Password reset error:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const isButtonDisabled = !isValid || isSubmitting;

  return (
    <AuthLayout>
      <Card
        className={cn(
          'rounded-2xl shadow-lg border-border transition-transform',
          'animate-fade-in-up',
          'motion-reduce:transition-none',
          // Dark mode: reduce shadow, subtle border for dialog-like appearance
          'dark:shadow-sm dark:border-[hsl(220_13%_18%)]',
          hasSubmitError && 'animate-shake',
          showSuccess && 'opacity-95 scale-[0.98]'
        )}
      >
        <CardContent className="pt-8 pb-6 px-8">
          <AuthHeader title={t('auth.signIn.title')} subtitle={t('auth.signIn.subtitle')} />

          <Form {...form}>
            <form
              action="#"
              onSubmit={form.handleSubmit(onSubmit, handleInvalidSubmit)}
              className="space-y-4"
              aria-busy={isSubmitting}
              noValidate
            >
              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('auth.signIn.email')}</FormLabel>
                    <FormControl>
                      <div className="input-focus-glow rounded-md transition-all relative">
                        <Input
                          type="email"
                          placeholder={t('auth.signIn.emailPlaceholder')}
                          autoComplete="email"
                          autoFocus
                          disabled={isSubmitting || showSuccess}
                          className="h-11 border-0 pr-10"
                          {...field}
                        />
                        {isEmailValid && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                            <Check className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage aria-live="polite" />
                  </FormItem>
                )}
              />

              {/* Password Field */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('auth.signIn.password')}
                    </FormLabel>
                    <FormControl>
                      <div className="input-focus-glow rounded-md transition-all">
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('auth.signIn.passwordPlaceholder')}
                            autoComplete="current-password"
                            disabled={isSubmitting || showSuccess}
                            className="h-11 pr-10 border-0"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isSubmitting || showSuccess}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            aria-label={
                              showPassword
                                ? t('auth.signIn.hidePassword')
                                : t('auth.signIn.showPassword')
                            }
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage aria-live="polite" />
                  </FormItem>
                )}
              />

              {/* Login Error Message */}
              {loginError && (
                <div
                  className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3"
                  role="alert"
                  aria-live="assertive"
                >
                  {loginError}
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between pt-1">
                <FormField
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal cursor-pointer">
                        {t('auth.signIn.rememberMe')}
                      </FormLabel>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground transition-colors"
                              aria-label={t('auth.signIn.rememberMe')}
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">{t('auth.signIn.rememberMeTooltip')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </FormItem>
                  )}
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPasswordModal(true);
                    // Pre-fill with login email if valid
                    if (isEmailValid) {
                      setResetEmail(emailValue);
                    }
                  }}
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#CDB4F8' }}
                  tabIndex={isSubmitting ? -1 : 0}
                >
                  {t('auth.signIn.forgotPassword')}
                </button>
              </div>

              {/* Sign In Button */}
              <Button
                type="submit"
                disabled={isButtonDisabled || showSuccess}
                className={cn(
                  'w-full h-11 rounded-xl font-medium transition-all duration-200',
                  isButtonDisabled || showSuccess
                    ? 'bg-[#E9D8FD] text-[#A78BFA] cursor-not-allowed hover:bg-[#E9D8FD]'
                    : 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white cursor-pointer shadow-lg shadow-[#C4B5FD]/40',
                  showSuccess && 'bg-green-500 hover:bg-green-500'
                )}
              >
                {showSuccess ? (
                  <>
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    {t('auth.signIn.success')}
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.signIn.submitting')}
                  </>
                ) : (
                  t('auth.signIn.submit')
                )}
              </Button>
            </form>
          </Form>

          {/* Divider with "or" text */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                {t('auth.signIn.orContinueWith')}
              </span>
            </div>
          </div>

          {/* Google Sign-In Button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading || isMicrosoftLoading || isSubmitting || showSuccess}
            className="w-full h-11 rounded-xl font-medium text-sm"
          >
            {isGoogleLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <GoogleIcon className="mr-2 h-5 w-5" />
            )}
            Inloggen met Google
          </Button>

          {/* Microsoft Sign-In Button */}
          {isFeatureEnabled('microsoftLogin') && (
            <Button
              type="button"
              variant="outline"
              onClick={handleMicrosoftSignIn}
              disabled={isMicrosoftLoading || isGoogleLoading || isSubmitting || showSuccess}
              className="w-full h-11 rounded-xl font-medium text-sm mt-3"
            >
              {isMicrosoftLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <MicrosoftIcon className="mr-2 h-5 w-5" />
              )}
              Inloggen met Microsoft
            </Button>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 px-8 pb-8">
          <Separator />

          {/* Sign Up Link */}
          <div className="text-center text-sm text-muted-foreground">
            {t('auth.signIn.noAccount')}{' '}
            <Link
              to="/signup"
              className="font-medium hover:underline"
              style={{ color: '#CDB4F8' }}
              tabIndex={isSubmitting ? -1 : 0}
            >
              {t('auth.signIn.createOne')}
            </Link>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground pt-2">
            {t('auth.signIn.footer')}
          </div>
        </CardFooter>
      </Card>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPasswordModal} onOpenChange={setShowForgotPasswordModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('auth.resetPassword.modalTitle')}</DialogTitle>
            <DialogDescription>{t('auth.resetPassword.modalDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="reset-email" className="text-sm font-medium">
                {t('auth.resetPassword.email')}
              </label>
              <Input
                id="reset-email"
                type="email"
                placeholder={t('auth.resetPassword.emailPlaceholder')}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={isResetting || resetSuccess}
                className="h-11"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isResetting && !resetSuccess) {
                    void handleForgotPassword();
                  }
                }}
              />
            </div>

            {resetError && (
              <div
                className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3"
                role="alert"
              >
                {resetError}
              </div>
            )}

            <Button
              onClick={handleForgotPassword}
              disabled={isResetting || resetSuccess || !resetEmail}
              className={cn(
                'w-full h-11 rounded-xl font-medium transition-all duration-200',
                isResetting || resetSuccess || !resetEmail
                  ? 'bg-[#E9D8FD] text-[#A78BFA] cursor-not-allowed hover:bg-[#E9D8FD]'
                  : 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white cursor-pointer shadow-lg shadow-[#C4B5FD]/40',
                resetSuccess && 'bg-green-500 hover:bg-green-500'
              )}
            >
              {resetSuccess ? (
                <>
                  <CheckCircle2 className="mr-2 h-5 w-5" />
                  {t('auth.resetPassword.success')}
                </>
              ) : isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.resetPassword.submitting')}
                </>
              ) : (
                t('auth.resetPassword.submit')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AuthLayout>
  );
}
